import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import {
  editUrl,
  imageUploadUrl,
  productViewerUrl,
  textTrackServiceURL,
  viewUrl,
  backendPoint,
  videoCreatorURL
} from "../config";
import { getCookie } from "./cookie";

const editDynoUrl = (id) => `${editUrl}/dynos/edit/${id}`;
const viewDynoUrl = (id) => `${viewUrl}/${id}`;
const openViewer = (productId) => `${productViewerUrl}/?productid=${productId}`;

const uploadImage = async (file, folder) => {
  const url = imageUploadUrl;
  const postUrl = process.env.REACT_APP_ADMIN_APP_GRAPHQL_ENDPOINT;
  const [extension] = file.type.split("/");
  const fileName = `${uuidv4()}.${extension}`;
  const imageSignatureRes = await fetch(postUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-access-token": getCookie("dyn_session"),
    },
    body: JSON.stringify({
      query: `
            {
              imageSignature {
                signature
                policy
                timestamp
              }
            }
          `,
    }),
  }).then((res) => res.json());

  const { signature, policy, timestamp } = await imageSignatureRes.data
    .imageSignature;

  const body = {
    "Content-Type": file.type,
    "x-amz-meta-uuid": "14365123651274",
    "x-amz-server-side-encryption": "AES256",
    "X-Amz-Credential": `AKIAV364KGUJS5KO5QOS/${moment()
      .utc()
      .format("YYYYMMDD")}/us-east-2/s3/aws4_request`,
    "X-Amz-Algorithm": "AWS4-HMAC-SHA256",
    "X-Amz-Date": timestamp,
    "x-amz-meta-tag": "",
    Policy: policy,
    "X-Amz-Signature": signature,
    key: `${folder}/${fileName}`,
    acl: "public-read",
    file: file,
  };
  const formData = new FormData();
  Object.keys(body).forEach((key) => formData.append(key, body[key]));

  await fetch(url, {
    method: "POST",
    body: formData,
  });

  return `${url}/${folder}/${fileName}`;
};

const validateImage = (file, minWidth = 150, minHeight = 70, maxWidth = 1200, maxHeight = 560) => {
  let _URL = window.URL || window.webkitURL;
  return new Promise((resolve, reject) => {
    let img = new Image();
    img.src = _URL.createObjectURL(file);
    img.onload = () => {
      if (img.width < minWidth || img.height < minHeight) {
        reject(`Image must be at least ${minWidth}x${minHeight} pixels.`);
      } else if(img.width > maxWidth || img.height > maxHeight) {
        reject(`Image must be smaller than ${maxWidth}x${maxHeight} pixels.`);
      } else {
        resolve(img);
      }
    };
    img.onerror = (err) => {
      reject(`Image must be at least ${minWidth}x${minHeight} pixels.`);
    };
  });
};
const getImage = (file, maxwidth, maxheight) => {
  let _URL = window.URL || window.webkitURL;
  return new Promise((resolve, reject) => {
    let img = new Image();
    img.src = _URL.createObjectURL(file);
    img.onload = () => {
      resolve(img);
    };
    img.onerror = (err) => {
      reject("Image size must be " + maxwidth + " X " + maxheight);
    };
  });
};

const fetchGql = (query) => {
  return fetch(backendPoint, {
    method: 'POST',
    body: JSON.stringify(query),
    headers: {
      'Content-Type': 'application/json',
      'x-access-token': getCookie("dyn_session"),
    },
  });
};

const getQueryParams = (params, url) => {
  let href = url;
  //this expression is to get the query strings
  let reg = new RegExp(`[?&]${params}=([^&#]*)`, "i");
  let queryString = reg.exec(href);
  return queryString ? queryString[1] : null;
};

const validateEmail = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

const validatePassword = (password) => {
  const re =
    // eslint-disable-next-line no-useless-escape
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[\^$*.\[\]{}\(\)?\-\"!@#%&\/,><\':;|_~`])\S{8,99}$/;
  return re.test(String(password));
};

const validatePhone = (phone) => {
  const re = /^\d{10}$/;
  return re.test(Number(phone));
};

const getDynoFieldsArguments = (node) => ({
  id: node.node.id,
  dynoTitle: node.node.dynoTitle,
  description: node.node.description,
  dynoPublishingStatus: node.node.dynoPublishingStatus,
  accessType: node.node.accessType,
  createdAt: node.node.createdAt,
  updatedAt: node.node.updatedAt,
  owner: node.node.owner,
  dynoType: node.node.dynoType,
  category: node.node.category,
});

const range = (start, end) => {
  let length = end - start + 1;
  /*
  	Create an array of certain length and set the elements within it from
    start value to end value.
  */
  return Array.from({ length }, (_, idx) => idx + start);
};

const uploadWithSasToken = async (id, folderType, file, uuid) => {
  try {
    // Request SAS token from the backend, passing the folder name as well
    const postUrl = process.env.REACT_APP_ADMIN_APP_GRAPHQL_ENDPOINT;
    const fileName = file.name.replace(/ /g, "_");
    const contentType = file.type || 'application/octet-stream';
    const generateSasToken = await fetch(postUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-access-token": getCookie("dyn_session"),
      },
      body: JSON.stringify({
        query: `
              query EdshopifyGenerateSasToken($where: EdshopifyGenerateSasTokenWhereInput!) {
                edshopifyGenerateSasToken(where: $where)
              }
            `,
        variables: {
          where: {
            id: id,
            folderType: folderType,
            fileName: fileName,
          }
        },
      }),
    }).then((res) => res.json());
    const sasUrl = await generateSasToken?.data?.edshopifyGenerateSasToken

    // Upload the file using the SAS token
    const uploadResponse = await fetch(sasUrl, {
      method: "PUT",
      headers: {
        "x-ms-blob-type": "BlockBlob",
        "x-ms-meta-uuid": uuid,
        'Content-Type': contentType,
      },
      body: file,
    });

    if (!uploadResponse.ok) {
      throw new Error(`File upload failed: ${uploadResponse.statusText}`);
    }
    return sasUrl;
  } catch (error) {
    console.error("Error during upload:", error);
    throw error;
  }
};

const uploadDALLEWithSasToken = async (id, folderType, imgUrl, fileName, uuid) => {
  try {
    const response = await fetch(imgUrl);
    const blob = await response.blob(); // image as Blob
    // Request SAS token from the backend, passing the folder name as well
    const postUrl = process.env.REACT_APP_ADMIN_APP_GRAPHQL_ENDPOINT;
    const contentType = blob.type || 'application/octet-stream';
    const generateSasToken = await fetch(postUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-access-token": getCookie("dyn_session"),
      },
      body: JSON.stringify({
        query: `
              query EdshopifyGenerateSasToken($where: EdshopifyGenerateSasTokenWhereInput!) {
                edshopifyGenerateSasToken(where: $where)
              }
            `,
        variables: {
          where: {
            id: id,
            folderType: folderType,
            fileName: `${fileName.toLowerCase().replace(/ /g, "_")}.${imgUrl.split('.').pop().split('?')[0]}` || `${fileName.toLowerCase().replace(/ /g, "_")}.png`,
          }
        },
      }),
    }).then((res) => res.json());

    const sasUrl = await generateSasToken?.data?.edshopifyGenerateSasToken

    // Upload the file using the SAS token
    const uploadResponse = await fetch(sasUrl, {
      method: "PUT",
      headers: {
        "x-ms-blob-type": "BlockBlob",
        "x-ms-meta-uuid": uuid,
        'Content-Type': contentType,
      },
      body: blob,
    });

    if (!uploadResponse.ok) {
      throw new Error(`File upload failed: ${uploadResponse.statusText}`);
    }
    return sasUrl;
  } catch (error) {
    console.error("Error during upload:", error);
    throw error;
  }
};

const uploadWithSasTokenAndProgress = async (id, folderType, file, onProgress, uuid, chunkSize = 0.5 * 1024 * 1024) => {
  try {
    // Step 1: Get SAS Token URL
    const postUrl = process.env.REACT_APP_ADMIN_APP_GRAPHQL_ENDPOINT;
    const fileName = file.name.replace(/ /g, "_");
    const contentType = file.type || "application/octet-stream";

    const generateSasToken = await fetch(postUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-access-token": getCookie("dyn_session"),
      },
      body: JSON.stringify({
        query: `
          query EdshopifyGenerateSasToken($where: EdshopifyGenerateSasTokenWhereInput!) {
              edshopifyGenerateSasToken(where: $where)
          }
        `,
        variables: {
          where: {
            id: id,
            folderType: folderType,
            fileName: fileName,
          },
        },
      }),
    }).then((res) => res.json());

    const sasUrl = await generateSasToken?.data?.edshopifyGenerateSasToken;

    if (!sasUrl) {
      throw new Error("Failed to get SAS URL");
    }

    // Step 2: Upload File in Chunks with Progress Tracking
    const blockIds = [];
    const totalChunks = Math.ceil(file.size / chunkSize);
    let uploadedBytes = 0;

    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);
      const blockId = btoa(i.toString().padStart(5, "0")); // Unique block ID, base64 encoded
      blockIds.push(blockId);

      const uploadUrl = `${sasUrl}&comp=block&blockid=${blockId}`;
      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "x-ms-blob-type": "BlockBlob",
          "x-ms-meta-uuid": uuid,
          "Content-Type": contentType,
        },
        body: chunk,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Chunk upload failed at part ${i}: ${uploadResponse.statusText}`);
      }
      // Update uploaded bytes & progress
      uploadedBytes += chunk.size;
      const progress = Math.round((uploadedBytes / file.size) * 100);
      if (onProgress) {
        onProgress(progress);
      }
    }

    // Step 3: Commit Blocks
    const blockListXml = `<?xml version="1.0" encoding="utf-8"?><BlockList>${blockIds.map((id) => `<Latest>${id}</Latest>`).join("")}</BlockList>`;
    const commitUrl = `${sasUrl}&comp=blocklist`;

    const commitResponse = await fetch(commitUrl, {
      method: "PUT",
      headers: {
        "x-ms-blob-content-type": contentType,
      },
      body: blockListXml,
    });

    if (!commitResponse.ok) {
      throw new Error(`Failed to commit blocks: ${commitResponse.statusText}`);
    }

    await fetch(`${sasUrl}&comp=metadata`, {
      method: "PUT",
      headers: {
        "x-ms-meta-uuid": uuid,
      },
    });

    if (onProgress) {
      onProgress(100); // Ensure progress is 100% after final commit
    }

    return sasUrl.split("?")[0]; // Return file URL without SAS token
  } catch (error) {
    console.error("Error during chunked upload:", error);
    throw error;
  }
};

const getFileBlob = async (file) => {
  if (file instanceof File) return file; // Already a File, return as-is

  if (file.uri) {
    const response = await fetch(file.uri);
    const blob = await response.blob();
    return new File([blob], file.name || "unknown", { type: file.type || blob.type });
  }

  throw new Error("Invalid file format");
};

const extractTextFromFile = (path, data) => {
  let newData = JSON.parse(data)
  newData = {...newData, origin: "edshop"}
  return fetch(`${textTrackServiceURL}/api/${path}`, {
    method: "POST",
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${getCookie("dyn_session")}` // Token added here
    },
    body: JSON.stringify(newData),
  });
}

async function callGenerateCourseOutline(payload) {
  const url = `${textTrackServiceURL}/api/outline/generate`; // Replace with your deployed Azure Function URL
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getCookie("dyn_session")}` // Token added here
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data.body
  } catch (error) {
  }
}
async function callGenerateDyno(payload) {
  const url = `${textTrackServiceURL}/api/content/generate`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getCookie("dyn_session")}` // Token added here
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data.body
  } catch (error) {
  }
}
async function generateVideo(payload) {
  try {
    const response = await fetch(videoCreatorURL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({...payload, project: "edshop"}),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data.body
  } catch (error) {
  }
}
async function getSourceChunkId(payload) {
  const url = `${textTrackServiceURL}/api/chunk_id`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data
  } catch (error) {
  }
}
async function generatePptx(payload) {
  try {
    const response = await fetch(`${videoCreatorURL}/api/generate-ppt-from-existing`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({...payload, project: "edshop"}),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data
  } catch (error) {
  }
}

// const uploadWithSasTokenAndProgress = async (id, folderType, file, onProgress, chunkSize = 4 * 1024 * 1024) => {
//   try {
//     const actualFile = await getFileBlob(file); // Ensure it's a valid File
//     const postUrl = process.env.ENDPOINT;
//     const fileName = actualFile.name;
//     const contentType = actualFile.type || "application/octet-stream";

//     const generateSasToken = await fetch(postUrl, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//         "x-access-token": getCookie("session"),
//       },
//       body: JSON.stringify({
//         query: `
//           query generateSASToken($where: where!) {
//             generateSASToken(where: $where)
//           }
//         `,
//         variables: {
//           where: {
//             id: id,
//             folderType: folderType,
//             fileName: fileName,
//           },
//         },
//       }),
//     }).then((res) => res.json());

//     const sasUrl = generateSasToken?.data?.generateSASToken;
//     if (!sasUrl) {
//       throw new Error("Failed to get SAS URL");
//     }

//     // Upload File in Chunks
//     const blockIds = [];
//     const totalChunks = Math.ceil(actualFile.size / chunkSize);
//     let uploadedBytes = 0;

//     for (let i = 0; i < totalChunks; i++) {
//       const start = i * chunkSize;
//       const end = Math.min(start + chunkSize, actualFile.size);
//       const chunk = actualFile.slice(start, end);
//       const blockId = btoa(i.toString().padStart(5, "0")); // Unique block ID, base64 encoded
//       blockIds.push(blockId);

//       const uploadUrl = `${sasUrl}&comp=block&blockid=${blockId}`;
//       const uploadResponse = await fetch(uploadUrl, {
//         method: "PUT",
//         headers: {
//           "x-ms-blob-type": "BlockBlob",
//           "Content-Type": contentType,
//         },
//         body: chunk,
//       });

//       if (!uploadResponse.ok) {
//         throw new Error(`Chunk upload failed at part ${i}: ${uploadResponse.statusText}`);
//       }

//       uploadedBytes += chunk.size;
//       const progress = Math.round((uploadedBytes / actualFile.size) * 100);
//       if (onProgress) {
//         onProgress(progress);
//       }
//     }

//     // Commit Blocks
//     const blockListXml = `<?xml version="1.0" encoding="utf-8"?><BlockList>${blockIds.map((id) => `<Latest>${id}</Latest>`).join("")}</BlockList>`;
//     const commitUrl = `${sasUrl}&comp=blocklist`;

//     const commitResponse = await fetch(commitUrl, {
//       method: "PUT",
//       headers: {
//         "x-ms-blob-content-type": contentType,
//       },
//       body: blockListXml,
//     });

//     if (!commitResponse.ok) {
//       throw new Error(`Failed to commit blocks: ${commitResponse.statusText}`);
//     }

//     if (onProgress) {
//       onProgress(100);
//     }

//     return sasUrl.split("?")[0]; // Return file URL without SAS token
//   } catch (error) {
//     console.error("Error during chunked upload:", error);
//     throw error;
//   }
// };


const vimeoUploadUrl = async (where) => {
  try {
    const postUrl = process.env.REACT_APP_ADMIN_APP_GRAPHQL_ENDPOINT;
    const res = await fetch(postUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-access-token": getCookie("dyn_session"),
      },
      body: JSON.stringify({
        query: `
              mutation EdshopifyVimeoUploadUrl($where: EdshopifyVimeoUploadUrlWhereInput!) {
                edshopifyVimeoUploadUrl(where: $where) {
                  id
                  uploadLink
                }
              }
            `,
        variables: {
          where: where
        },
      }),
    }).then((res) => res.json());
    const {id, uploadLink} = await res?.data?.edshopifyVimeoUploadUrl
    return {id, uploadLink};
  } catch (error) {
    console.error("Error during upload:", error);
    throw error;
  }
};

const vimeoVideo = async (id, videoId) => {
  try {
    const postUrl = process.env.REACT_APP_ADMIN_APP_GRAPHQL_ENDPOINT;
    const res = await fetch(postUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-access-token": getCookie("dyn_session"),
      },
      body: JSON.stringify({
        query: `
              query edshopifyVideo($where: EdshopifyVideoWhereInput!) {
                edshopifyVideo(where: $where) {
                  id
                  url
                  name
                  duration
                  thumbnail
                  description
                  tags {
                    tag
                  }
                  status
                }
              }
            `,
        variables: {
          where: {
            id,
            videoId
          }
        },
      }),
    }).then((res) => res.json());
    const video = await res?.data?.edshopifyVideo
    return video;
  } catch (error) {
    console.error("Error fetching video:", error);
    throw error;
  }
};

const createAbortError = () => {
  if (typeof DOMException === "function") {
    return new DOMException("Aborted", "AbortError");
  }
  const error = new Error("Aborted");
  error.name = "AbortError";
  return error;
};

const sleep = (ms, signal) => {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);

    const onAbort = () => {
      clearTimeout(timeoutId);
      signal?.removeEventListener("abort", onAbort);
      reject(createAbortError());
    };

    if (signal) {
      signal.addEventListener("abort", onAbort);
    }
  });
};

const fetchVimeoTextTracks = async (videoId, { signal } = {}) => {
  if (!videoId) return [];
  try {
    const response = await fetch(`https://player.vimeo.com/video/${videoId}/config`, { signal });
    if (!response.ok) {
      throw new Error(`Failed to fetch Vimeo config: ${response.status}`);
    }
    const payload = await response.json();
    const tracks = payload?.request?.text_tracks || payload?.text_tracks || [];
    return Array.isArray(tracks) ? tracks : [];
  } catch (error) {
    if (error.name === "AbortError") {
      throw error;
    }
    console.error(`Error fetching Vimeo text tracks for ${videoId}:`, error);
    return [];
  }
};

const waitForVimeoTextTracks = async (videoId, { pollInterval = 45000, maxAttempts = 15, signal } = {}) => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const textTracks = await fetchVimeoTextTracks(videoId, { signal });
    if (textTracks.length > 0) {
      return textTracks;
    }
    if (attempt < maxAttempts - 1) {
      await sleep(pollInterval, signal);
    }
  }
  throw new Error("Timed out waiting for Vimeo text tracks.");
};

const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return "00:00";

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  
  return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
};

const extractDetailsFromUrl = (url) => {
  const regex = /https:\/\/[^/]+\.blob\.core\.windows\.net\/([^/]+)\/([^/]+)\/(.+)/;
  const match = url.match(regex);

  if (match) {
    // Extract the userId, folderName, and fileName
    const [, id, folderName, fileName] = match;
    return { id, folderName, fileName };
  } else {
    throw new Error("URL format is incorrect or does not match the expected pattern.");
  }
}

const generateVideoThumbnail = (file) => {
  return new Promise((resolve, reject) => {
    if(Object.keys(file).length === 0 && file.size === 0) {
      reject({message: "File not found. Please select file again."})
    }
    const video = document.createElement("video");
    video.src = URL.createObjectURL(file);
    video.crossOrigin = "anonymous"; // Prevent CORS issues
    video.muted = true;
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      video.currentTime = Math.min(1, video.duration / 2); // Seek to 1s or midpoint
    };
    
    video.onseeked = () => {
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Convert canvas to image URL
      const thumbnail = canvas.toDataURL("image/jpeg");
      resolve(thumbnail);
    };

    video.onerror = (error) => reject(error);
  });
}

const cleanHtml = (str) => {
  if(str) {
    let html = str
    try {
      if (typeof html === "string" && html.startsWith('"') && html.endsWith('"')) {
        html = JSON.parse(html);
      }
      return html.replace(/\\\"/g, '"').replace(/\\\\/g, "\\");
    } catch (error) {
      console.error("Error cleaning HTML:", error);
      return html;
    }
  }
};

const fileSize = (file) => {
  if((file.size / 1024 / 1024).toFixed(1) === "0.0")
    return `${(file.size/1024).toFixed(1)} KB`

  return `${(file.size / 1024 / 1024).toFixed(2)} MB`
}

function generateOfficeEmbedLink(pptxUrl) {
  const encodedUrl = encodeURIComponent(pptxUrl);
  return `<iframe 
      src="https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}"
      width="500" height="500" allow="fullscreen" loading="lazy" style="border: 1px solid #ccc" frameborder="0">
    </iframe>`
}

async function createVideoFromPPt(payload) {
  try {
    const response = await fetch(`${videoCreatorURL}/api/finalize-video-from-ppt`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({...payload, project: "edshop"}),
    });

    const {job_id} = await response.json();
    return job_id
  } catch (error) {
    console.error("Error creating Job:", error);
  }
}

async function pollJobStatus({
  jobId,
  jobKey,
  interval = 15000,
  onStatusUpdate,
  checkStatusFn,
  onFinalPostRequest,
  stopStatuses = ['completed', 'failed']
}) {
  let stopped = false;

  const poll = async () => {
    if (stopped) return;

    const data = await checkStatusFn(jobId);
    if(data?.status === 'in_progress') onStatusUpdate('processing');

    if (data?.status === 'completed') {
      try {
        await onFinalPostRequest?.(data?.result);
      } catch (err) {
        console.error(`gql post request failed for ${jobKey}:`, err);
      }
    }

    if (!stopStatuses.includes(data?.status)) {
      setTimeout(poll, interval);
    }
  };

  poll();

  return () => { stopped = true }; // cancellation function
};

function getVimeoVideoId(url) {
  const regex = /vimeo\.com\/(?:.*\/)?(?:videos?\/)?(\d+)/;
  const match = url.match(regex);
  return match ? match[1] : null;
}

export {
  editDynoUrl,
  viewDynoUrl,
  openViewer,
  uploadImage,
  validateImage,
  getQueryParams,
  validateEmail,
  validatePassword,
  validatePhone,
  getDynoFieldsArguments,
  range,
  uploadWithSasToken,
  extractDetailsFromUrl,
  vimeoUploadUrl,
  vimeoVideo,
  formatDuration,
  generateVideoThumbnail,
  cleanHtml,
  fileSize,
  uploadWithSasTokenAndProgress,
  extractTextFromFile,
  callGenerateCourseOutline,
  uploadDALLEWithSasToken,
  fetchGql,
  callGenerateDyno,
  getImage,
  generateVideo,
  getSourceChunkId,
  generatePptx,
  generateOfficeEmbedLink,
  createVideoFromPPt,
  getVimeoVideoId,
  pollJobStatus,
  waitForVimeoTextTracks
};
